import { useState, useCallback, useRef, useEffect } from "react";
import { useNotification } from "@refinedev/core";
import {
  getActiveJob,
  startExportJob,
  startImportJob,
  downloadJobResult,
  validateCSVFile,
  validateFileBasic,
  cancelJob,
  JobData,
  ExportRequest,
  getJobStatus,
  getJobHistory,
  triggerFileDownload,
  ValidationResult,
} from "../../api/import_export";

interface UseImportExportJobsOptions {
  onJobCompleted?: (job: JobData) => void;
}

export const useImportExportJobs = (options?: UseImportExportJobsOptions) => {
  const { open: notify } = useNotification();

  const [currentJob, setCurrentJob] = useState<JobData | null>(null);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [jobHistory, setJobHistory] = useState<JobData[]>([]);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingJobIdRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);

  // Keep options stable without re-creating callbacks
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollingJobIdRef.current = null;
      retryCountRef.current = 0;
    };
  }, []);

  const fetchJobHistory = useCallback(async () => {
    try {
      const jobHistoryData = await getJobHistory(6);
      setJobHistory(jobHistoryData);
    } catch (error) {
      console.error("Error fetching job history:", error);
    }
  }, []);

  // Stop current polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingJobIdRef.current = null;
    retryCountRef.current = 0;
  }, []);

  // Start polling for a specific job
  const startJobPolling = useCallback(
    (jobId: string) => {
      // Already polling this job? do nothing
      if (pollingJobIdRef.current === jobId) return;

      // Stop any existing polling
      stopPolling();

      pollingJobIdRef.current = jobId;
      retryCountRef.current = 0;

      const poll = async () => {
        try {
          const jobData = await getJobStatus(jobId);
          setCurrentJob(jobData);
          retryCountRef.current = 0; // Reset retry count on success

          const status = jobData.status?.toLowerCase();
          if (
            status === "completed" ||
            status === "failed" ||
            status === "cancelled"
          ) {
            // Job finished - stop polling
            stopPolling();

            // Fire completion callback
            optionsRef.current?.onJobCompleted?.(jobData);

            // Refresh history
            fetchJobHistory();
          }
        } catch (error) {
          retryCountRef.current++;
          console.error(
            `Error polling job ${jobId} (attempt ${retryCountRef.current}):`,
            error
          );

          // Stop polling after 3 failed attempts
          if (retryCountRef.current >= 3) {
            console.error(
              `Stopping polling for job ${jobId} after 3 failed attempts`
            );
            stopPolling();
          }
        }
      };

      // Start polling immediately, then every 2 seconds
      poll();
      pollingIntervalRef.current = setInterval(poll, 2000);
    },
    [stopPolling, fetchJobHistory]
  );

  // Does not depend on startJobPolling identity (it’s stable now anyway)
  const checkActiveJob = useCallback(async () => {
    try {
      const activeJob = await getActiveJob();
      if (activeJob) {
        setCurrentJob((prev) => (prev?.id === activeJob.id ? prev : activeJob));
        setShowJobDialog(true);

        // Only (re)start if we're not already polling this job
        startJobPolling(activeJob.id);
        return activeJob;
      }
      return null;
    } catch (error) {
      console.error("Error checking active job:", error);
      return null;
    }
  }, [startJobPolling]);

  const handleExport = useCallback(
    async (exportRequest: ExportRequest) => {
      try {
        const activeJob = await checkActiveJob();
        if (activeJob) return;

        const result = await startExportJob(exportRequest);
        const jobData = await getJobStatus(result.job_id);

        setCurrentJob(jobData);
        setShowJobDialog(true);
        startJobPolling(result.job_id);

        notify?.({
          message: "Export job started successfully",
          type: "success",
        });
      } catch (error: unknown) {
        console.error("Error starting export:", error);
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        notify?.({
          message:
            axiosError?.response?.data?.message || "Failed to start export",
          type: "error",
        });
      }
    },
    [checkActiveJob, startJobPolling, notify]
  );

  const handleImport = useCallback(
    async (file: File) => {
      try {
        const basicValidation = validateFileBasic(file);
        if (!basicValidation.valid) {
          notify?.({
            message: basicValidation.error || "Invalid file",
            type: "error",
          });
          return false;
        }

        const activeJob = await checkActiveJob();
        if (activeJob) return false;

        const result = await startImportJob(file);
        const jobData = await getJobStatus(result.job_id);

        setCurrentJob(jobData);
        setShowJobDialog(true);
        startJobPolling(result.job_id);

        notify?.({
          message: "Import job started successfully",
          type: "success",
        });
        return true;
      } catch (error: unknown) {
        console.error("Error starting import:", error);
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        notify?.({
          message:
            axiosError?.response?.data?.message || "Failed to start import",
          type: "error",
        });
        return false;
      }
    },
    [checkActiveJob, startJobPolling, notify]
  );

  const handleDownloadResult = useCallback(
    async (jobId: string) => {
      try {
        const result = await downloadJobResult(jobId);
        triggerFileDownload(result.download_url, result.filename);
        notify?.({
          message: `Download started: ${result.filename}`,
          type: "success",
        });
      } catch (error: unknown) {
        console.error("Error downloading result:", error);
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        notify?.({
          message:
            axiosError?.response?.data?.message || "Failed to download result",
          type: "error",
        });
      }
    },
    [notify]
  );

  const handleCloseJobDialog = useCallback(() => {
    setShowJobDialog(false);
    stopPolling();
    setCurrentJob(null);
  }, [stopPolling]);

  // Handler to pick and monitor a specific job from history
  const handlePickJob = useCallback(
    async (jobId: string) => {
      try {
        if (jobId === "") {
          handleCloseJobDialog();
          return;
        }

        const jobData = await getJobStatus(jobId);
        setCurrentJob(jobData);
        setShowJobDialog(true);

        // If job is still active, start polling
        const status = jobData.status?.toLowerCase();
        if (
          status === "pending" ||
          status === "validating" ||
          status === "processing"
        ) {
          startJobPolling(jobId);
        }
      } catch (error) {
        console.error("Error picking job:", error);
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        notify?.({
          message: axiosError?.response?.data?.message || "Failed to load job",
          type: "error",
        });
      }
    },
    [startJobPolling, notify]
  );

  const validateImportFile = useCallback(
    async (file: File): Promise<ValidationResult> => {
      try {
        const basicValidation = validateFileBasic(file);
        if (!basicValidation.valid) {
          return { success: false, error: basicValidation.error };
        }
        const result = await validateCSVFile(file);
        return result;
      } catch (error: unknown) {
        // Since validation errors now return 200 status, they shouldn't reach this catch block
        // But handle any unexpected errors gracefully
        return {
          success: false,
          error: error instanceof Error ? error.message : String(error),
        };
      }
    },
    []
  );

  const handleCancelJob = useCallback(
    async (jobId: string) => {
      try {
        await cancelJob(jobId);
        stopPolling();

        // Update current job to cancelled state
        setCurrentJob((prev) =>
          prev?.id === jobId ? { ...prev, status: "cancelled" } : prev
        );

        notify?.({
          message: "Job cancelled successfully",
          type: "success",
        });

        // Refresh job history
        fetchJobHistory();
      } catch (error: unknown) {
        console.error("Error cancelling job:", error);
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        notify?.({
          message:
            axiosError?.response?.data?.message || "Failed to cancel job",
          type: "error",
        });
      }
    },
    [stopPolling, notify, fetchJobHistory]
  );

  // Run once on mount to check for active jobs and load history
  useEffect(() => {
    (async () => {
      try {
        const activeJob = await getActiveJob();
        if (activeJob) {
          setCurrentJob(activeJob);
          setShowJobDialog(true);
          startJobPolling(activeJob.id);
        }
        await fetchJobHistory();
      } catch (error) {
        console.error("Error checking active job on mount:", error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty

  return {
    currentJob,
    showJobDialog,
    jobHistory,
    handleExport,
    handleImport,
    handleCancelJob,
    handleDownloadResult,
    handleCloseJobDialog,
    handlePickJob,
    validateImportFile,
    fetchJobHistory,
    checkActiveJob,
    stopPolling,
  };
};
